import React, { useState } from "react";
import { useNotify, useRefresh, Button as RaButton, usePermissions } from "react-admin";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
  Box,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const getToken = () => JSON.parse(localStorage.getItem("token"));

const CSV_COLUMNS = [
  "name",
  "phonenumber",
  "password",
  "state_id",
  "locality_id",
  "gender",
  "dob",
  "crop_ids",
  "service_ids",
  "fc_name",
  "crops",
];

const SAMPLE_ROW = [
  "Ahmed Ali",
  "+249911234567",
  "Passw0rd!",
  "1",
  "2",
  "male",
  "1990-05-20",
  "1;2",
  "3",
  "John Coordinator",
  "Sorghum, Millet",
];

const downloadTemplate = () => {
  const csv = [CSV_COLUMNS.join(","), SAMPLE_ROW.join(",")].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "beneficiaries_template.csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const uploadCsv = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(
    `${process.env.REACT_APP_URL}/admin/beneficiaries/bulk/csv`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body: formData,
    }
  );

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || "Failed to upload CSV");
  }
  return data;
};

const BulkUploadBeneficiaries = () => {
  const { permissions } = usePermissions();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState("select"); // select | uploading | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const notify = useNotify();
  const refresh = useRefresh();

  if (!["admin", "operater"].includes(permissions)) return null;

  const handleClose = () => {
    if (stage === "uploading") return;
    setOpen(false);
    setFile(null);
    setStage("select");
    setResult(null);
    setError(null);
  };

  const handleFileSelected = (e) => {
    const selected = e.target.files?.[0] || null;
    setError(null);
    setResult(null);
    if (selected && !selected.name.toLowerCase().endsWith(".csv")) {
      setError("Only .csv files are accepted");
      setFile(null);
      return;
    }
    if (selected && selected.size > MAX_SIZE_BYTES) {
      setError("File exceeds the 5MB size limit");
      setFile(null);
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setError(null);
    try {
      setStage("uploading");
      const data = await uploadCsv(file);
      setResult(data);
      setStage("done");
      notify(
        `${data.createdCount} beneficiary(ies) created, ${data.skippedCount} skipped`,
        { type: data.skippedCount ? "warning" : "success" }
      );
      refresh();
    } catch (err) {
      setError(err.message || "Something went wrong");
      setStage("select");
    }
  };

  const busy = stage === "uploading";

  return (
    <>
      <RaButton
        label="Bulk Upload CSV"
        onClick={() => setOpen(true)}
        startIcon={<UploadFileIcon />}
      />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>Bulk Upload Beneficiaries (CSV)</DialogTitle>
        <DialogContent dividers>
          {stage === "select" && (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Upload a .csv file (max 5MB) with a header row. Expected columns:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", mb: 1, wordBreak: "break-all" }}
              >
                {CSV_COLUMNS.join(", ")}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                crop_ids and service_ids should contain semicolon-separated
                integers within their cell (e.g. 1;2;3), since commas already
                separate columns. Optional columns may be left empty.
              </Typography>
              <Link
                component="button"
                variant="body2"
                type="button"
                onClick={downloadTemplate}
                sx={{ display: "inline-block", mb: 2 }}
              >
                Download sample template
              </Link>
              <Box>
                <input type="file" accept=".csv" onChange={handleFileSelected} />
              </Box>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}

          {busy && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Uploading and processing CSV...
              </Typography>
              <LinearProgress />
            </>
          )}

          {stage === "done" && result && (
            <>
              <Alert
                severity={result.skippedCount ? "warning" : "success"}
                sx={{ mb: 2 }}
              >
                {result.totalRows} row(s) processed — {result.createdCount}{" "}
                created, {result.skippedCount} skipped
              </Alert>
              {result.skipped?.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Skipped rows
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.skipped.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell>{s.row}</TableCell>
                          <TableCell>{s.phonenumber}</TableCell>
                          <TableCell>{s.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {stage === "done" ? (
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={busy || !file}
              >
                Upload
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadBeneficiaries;
